import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, Phone, ArrowRight } from "lucide-react";
import { CTAButton } from "@/components/shared/CTAButton";
import { apiClient } from "@/lib/api";
import { SEO, buildBreadcrumbSchema } from "@/components/seo/SEO";
import { Watermark } from "@/components/shared/Watermark";
import { MotionReveal } from "@/components/shared/MotionReveal";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { CaseEvaluationDialog } from "@/components/home/CaseEvaluationDialog";
import { FIRM, INSIGHT_CATEGORY_IMAGES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

export default function InsightDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { t, isSpanish, getInsightOverride } = useLanguage();
  const categoryLabels = t("insightsList.categories");

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    apiClient
      .get(`/insights/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    apiClient
      .get("/insights", { params: { category: post.category } })
      .then((res) => setRelated(res.data.filter((p) => p.slug !== post.slug).slice(0, 3)))
      .catch(() => {});
  }, [post]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <h1 className="font-heading text-2xl font-semibold text-[color:var(--navy-950)]">{t("insightDetail.notFoundHeading")}</h1>
        <div className="mt-6 flex justify-center">
          <CTAButton variant="primary" onClick={() => navigate("/insights")}>{t("insightDetail.backToInsights")}</CTAButton>
        </div>
      </div>
    );
  }

  if (loading || !post) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <div className="h-8 w-8 border-2 border-[color:var(--gold-500)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Spanish overlay for title/excerpt/meta/sections (Insights are in the
  // core-page translation scope). Falls back to English backend content
  // whenever a translated section isn't available.
  const override = getInsightOverride(post.slug);
  const displayTitle = override?.title || post.title;
  const displayMetaDescription = override?.metaDescription || post.meta_description;
  const displaySections = override?.sections || post.sections;

  return (
    <article data-testid="insight-detail-page">
      <SEO
        title={displayTitle}
        description={displayMetaDescription}
        path={`/insights/${post.slug}`}
        type="article"
        jsonLd={buildBreadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Insights", path: "/insights" },
          { name: post.title, path: `/insights/${post.slug}` },
        ])}
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden">
        <Watermark word={post.category.split(" ")[0]} position="top-right" className="text-white top-2" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <Breadcrumbs
            items={[
              { label: t("insightDetail.breadcrumbHome"), to: "/" },
              { label: t("insightDetail.breadcrumbInsights"), to: "/insights" },
              { label: displayTitle, to: `/insights/${post.slug}` },
            ]}
            className="mb-6"
          />
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{isSpanish ? (categoryLabels[post.category] || post.category) : post.category}</span>
          <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-white leading-tight text-balance">{displayTitle}</h1>
          <div className="mt-6 flex items-center gap-5 text-sm text-slate-400">
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {post.published_date}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {post.read_time_minutes} {t("insightDetail.minRead")}</span>
          </div>
        </div>
      </header>

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-12 z-10">
        <div className="rounded-[1.75rem] overflow-hidden shadow-[0_30px_70px_rgba(11,18,32,0.18)] ring-1 ring-slate-100">
          <img
            src={INSIGHT_CATEGORY_IMAGES[post.category] || INSIGHT_CATEGORY_IMAGES.Immigration}
            alt={`${isSpanish ? (categoryLabels[post.category] || post.category) : post.category} — ${displayTitle}`}
            className="w-full h-56 sm:h-80 object-cover"
            loading="eager"
            data-testid="insight-detail-featured-image"
          />
        </div>
      </div>

      <section className="pt-10 sm:pt-12 pb-16 sm:pb-20 bg-[color:var(--paper)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal>
            <div className="prose prose-slate max-w-none prose-headings:font-heading prose-headings:text-[color:var(--navy-950)] prose-p:text-slate-600 prose-p:leading-7 prose-a:text-[color:var(--navy-950)] prose-li:text-slate-600">
              {displaySections.map((section, idx) => (
                <div key={section.heading || section.paragraphs[0]} className="mb-6">
                  {section.heading && (
                    <h2 className="text-xl sm:text-2xl mt-10 mb-3 pb-2 border-b border-slate-100">{section.heading}</h2>
                  )}
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={p} className={idx === 0 && pIdx === 0 ? "text-lg text-slate-700 leading-8" : undefined}>{p}</p>
                  ))}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul>
                      {section.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </MotionReveal>

          {related.length > 0 && (
            <div className="mt-16" data-testid="related-articles-section">
              <h3 className="font-heading text-xl text-[color:var(--navy-950)] mb-5">{t("insightDetail.relatedHeading")}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {related.map((r) => {
                  const rOverride = getInsightOverride(r.slug);
                  return (
                    <Link
                      key={r.slug}
                      to={`/insights/${r.slug}`}
                      className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-5 hover:border-slate-300 hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)] hover:-translate-y-0.5 transition-all duration-300"
                      data-testid="related-article-card"
                    >
                      <span className="text-xs uppercase tracking-[0.15em] text-slate-500">{isSpanish ? (categoryLabels[r.category] || r.category) : r.category}</span>
                      <h4 className="mt-2 font-heading text-sm text-[color:var(--navy-950)] leading-snug group-hover:text-[color:var(--gold-500)] transition-colors">
                        {rOverride?.title || r.title}
                      </h4>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-[color:var(--navy-950)]">
                        {t("insightDetail.readMore")} <ArrowRight className="h-3 w-3" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          <div className="relative mt-14 rounded-[2rem] border border-slate-200 bg-[color:var(--sand)] p-8 sm:p-10 text-center overflow-hidden">
            <h3 className="font-heading text-xl text-[color:var(--navy-950)] mb-2">{t("insightDetail.ctaHeading")}</h3>
            <p className="text-sm text-slate-600 mb-6 max-w-md mx-auto">
              {t("insightDetail.ctaBody")}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <CaseEvaluationDialog
                trigger={<CTAButton variant="primary">{t("insightDetail.ctaPrimary")}</CTAButton>}
              />
              <a href={`tel:${FIRM.phoneTel}`}>
                <CTAButton variant="outlineLight" icon={Phone}>{FIRM.phoneDisplay}</CTAButton>
              </a>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
}
